/**
 * Get a full URL for an image based on entity type and path
 */
export const getImageUrl = (entityType, imagePath) => {
  if (!imagePath) {
    const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const backendOrigin = apiBase.replace(/\/api$/, '');
    return `${backendOrigin}/uploads/products/noimage.png`;
  }

  // If it's already a full URL or absolute path, return as is
  if (typeof imagePath === 'string' && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
    try {
      const u = new URL(imagePath);
      // Force relative path (e.g., /uploads/xyz.jpg)
      return u.pathname.startsWith('/') ? u.pathname : `/${u.pathname}`;
    } catch {
      // Fallback: return as-is if URL parse fails
      return imagePath;
    }
  }
  if (typeof imagePath === 'string' && imagePath.startsWith('/')) {
    return imagePath;
  }

  // For makes, build path under uploads/makes unless already absolute
  if (entityType === 'makes') {
    const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const backendOrigin = apiBase.replace(/\/api$/, '');
    return `${backendOrigin}/uploads/makes/${imagePath}`;
  }

  // For other entities, use backend origin and entity subfolder
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const backendOrigin = apiBase.replace(/\/api$/, '');
  return `${backendOrigin}/uploads/${entityType}/${imagePath}`;
};

// Central helper for backend-hosted noimage path
export const getNoImageUrl = () => {
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const backendOrigin = apiBase.replace(/\/api$/, '');
  return `${backendOrigin}/uploads/products/noimage.png`;
};

/**
 * Normalize a SKU by replacing spaces with underscores
 */
export const normalizeSkuForImage = (sku) => {
  if (!sku || typeof sku !== 'string') return '';
  // 1) Replace spaces with underscores
  // 2) Remove forward and back slashes which cannot exist in filenames
  // 3) Trim stray underscores
  return sku
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[\\/]+/g, '')
    .replace(/^_+|_+$/g, '');
};

/**
 * Resolve a single DB image field value (e.g. "FT102.jpg", "/uploads/products/FT102.jpg",
 * or a full URL) to a renderable URL.  Returns null if input is empty/falsy.
 */
export const resolveProductImagePath = (imageValue) => {
  if (!imageValue || (typeof imageValue === 'string' && !imageValue.trim())) return null;
  const val = String(imageValue).trim();

  // Full external URL
  if (val.startsWith('http://') || val.startsWith('https://')) {
    // Leave absolute URLs untouched so images hosted on CDN/backoffice domains load correctly
    return val;
  }

  // Already an absolute path
  if (val.startsWith('/')) return val;

  // Bare filename — prepend uploads path
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const backendOrigin = apiBase.replace(/\/api$/, '');
  return `${backendOrigin}/uploads/products/${val}`;
};

/**
 * Get all valid image URLs for a product from its image1–image4 DB fields.
 * Only returns URLs for slots that have a value set in the database.
 * Does NOT guess or construct URLs from SKU.
 */
export const getProductImageUrls = (product) => {
  if (!product) return [];
  const urls = [];
  ['image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8'].forEach(field => {
    const url = resolveProductImagePath(product[field]);
    if (url) urls.push(url);
  });
  return urls;
};

/**
 * Get a single product thumbnail URL from the product's image1 field.
 * Returns noimage URL if image1 is not set.
 * Accepts a product object (with image1 field) or a plain image path string.
 */
export const getProductThumbnailUrl = (product) => {
  if (!product) return getNoImageUrl();
  // Allow passing a plain string (an image path) for backward-compat convenience
  if (typeof product === 'string') {
    const url = resolveProductImagePath(product);
    return url || getNoImageUrl();
  }
  // Try image1 first, then fall through image2–image8 for the first available image
  for (const field of ['image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8']) {
    const url = resolveProductImagePath(product[field]);
    if (url) return url;
  }
  return getNoImageUrl();
};
